import moment from 'moment';
import { ReactElement } from 'react';
import { StakeholderTypes } from './Constants';
import { Theme } from 'src/design-system/theme';

type ColumnAlign = 'left' | 'right';
type ColumnFixed = boolean | ColumnAlign;

interface TableColumn<T> {
  key: string;
  align?: ColumnAlign | 'center';
  width?: NumberString;
  fixed?: ColumnFixed;
  dataIndex: string;
  textWrap?: string;
  padding?: number;
  title: string | ReactElement;
  ellipsis?: boolean;
  className?: string;
  render: (key: string, record: T, index?: number) => React.ReactNode;
}

type ModulePermissions = Record<string, Record<string, boolean> | boolean>;

type ShippingLine = {
  id: string;
  name: string;
  key: string;
  logoUrl?: string;
  isTrackingEnabled?: boolean;
  type?: string;
  dbId?: string;
  isBookingSupported?: boolean;
};

type AccessTypes = 'TEAM' | 'PUBLIC' | 'PRIVATE';

type AccessType = {
  accessType: AccessTypes;
  teamId?: string;
};

type AccessTypeFilter = {
  accessType: AccessTypes;
  key?: string;
};

type NextOrPrev = 'first' | 'prev' | 'next';

type NumberString = number | string;

type ShipmentTypes = 'SHIPMENT' | 'CONTAINER' | 'BL';

type InquiryTypes = 'FCL' | 'LCL' | 'AIRCARGO';

interface RouterProps<Params = any> {
  history: any;
  location: any;
  match: {
    url: any;
    params: Params;
    isExact?: boolean;
  };
}

type IdName = {
  id: string;
  name: string;
};

type KeyLabel = {
  key: string;
  label: string;
};

type KeyValue = {
  key: string;
  value: string;
};

type LabelValue = {
  label: string;
  value: string;
};

type KeyValueMap = {
  [key: string]: string;
};

type DefaultSortBy = {
  sortBy?: string;
  descendingOrder?: boolean;
};

interface UserData {
  organisation_id: string;
  type: string;
  id: NumberString;
  username: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  ff_id: string;
  ff_name: string;
  privacy_policy_accepted: boolean;
}

interface Address {
  id: string;
  locationType: string;
  name: string;
  address?: string;
  organisationId: string;
  city?: string;
  countryId?: string;
  state?: string;
  pincode?: string;
  landmark?: string;
  displayAddress: string;
  inchargeUserId?: string;
  addressTypes?: string[];
  inchargeContactName?: string;
  inchargeContactEmail?: string;
  inchargeContactPhone?: string;
  isActive: boolean;
  location?: string;
  referenceNumber?: string;
  countryName?: string;
  countryCode?: string;
  flagS3Link?: string;
  latitude?: number | string;
  longitude?: number | string;
}
interface StylesProps<T = {}> {
  classes: {
    [X in keyof T]: string;
  };
  theme: Theme & {
    bodyBackground?: string;
    primaryColor?: string;
    orgPrimayColor?: string;
    errorColor?: string;
  };
}

interface APIResponseType<DataType> {
  isSuccess: boolean;
  data?: DataType;
  errorMessage?: string;
  filename?: string;
}

type Styles<TGetStyles extends (...args: any) => any> = {
  [X in keyof ReturnType<TGetStyles>]: React.CSSProperties;
};

interface Carrier {
  id: string;
  name: string;
  logoUrl: string;
  isTrackingEnabled: boolean;
  type: string; // TODO
  dbId: string;
  isBookingSupported: boolean;
  scacCode?: string;
  termsAndConditions?: string;
}

interface PaginationData {
  currentPageNumber: number;
  isNextPresent: boolean;
  lastItemIdNext: NumberString;
  lastItemIdPrev: NumberString;
  lastSortedColumnValueNext: moment.MomentInput;
  lastSortedColumnValuePrev: moment.MomentInput;
  nextOrPrev: NextOrPrev;
  resultPerPage: number;
  sortBy: string;
}

interface PaginationActions {
  setFilter: any;
  cancelFilter: any;
  applyFilter: any;
  resetFilter: any;
  setPagination: any;
  setPrevPagination: any;
  reload: any;
}

type ThenArg<T> = T extends PromiseLike<infer U> ? U : T;

type KeyLabelObject = {
  key: number | string;
  label: string;
};

interface ContainerDetails {
  count: number;
  size: string;
  temperature?: string;
  type: string;
  weight?: Weight;
  packageDimension?: PackageDimension;
  volume?: Weight;
}

interface Weight {
  unit: string;
  value: number;
}

interface PackageDimension {
  breadth: number;
  height: number;
  length: number;
  unit: string;
}

interface InquiryPortDetails {
  address?: string;
  countryCode?: string;
  countryName?: string;
  movementType?: string;
  port: string;
  services: string[];
  haulage: string;
  preferredWetPort?: string;
}

interface ProductDetails {
  id: string;
  hsCode: string;
  internalName: string;
  description: string;
  name: string;
}

interface ProductAdditionalDetails {
  ventDetails?: string;
  ventSetting?: string;
  relativeHumidity?: number;
  temperatureControlled?: number;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  emsNumber?: string;
  flashPointTemp?: number;
  imoClassCode?: string;
  packingGroup?: string;
  undgNumber?: string;
  shippingName: string;
}

interface PaymentDetails {
  chargeTypes: string;
  freightTerms: string;
  payer: string;
  name: number;
}

interface NotifyUsers {
  name: string;
  email: string;
}

interface ReeferProductTempRange {
  label: Range;
}

interface BookingMasterData {
  chargeTypes: KeyLabelObject;
  payer: KeyLabelObject;
  freightTerms: KeyLabelObject;
}
type Team = {
  default_execution_access: boolean;
  id: string;
  internal_organisation_id: string;
  name: string;
  organisation_id: string;
  remark: string;
  user_id: string;
  use_for_stakeholders?: boolean;
};

interface LoginSettings {
  mobile_number_non_mandatory?: boolean;
  strategies?: {
    google?: boolean;
    saml?: boolean;
    password?: boolean;
  };
  password_policy?: {
    strong_password?: boolean;
    multi_factor?: string | null; // email
  };
  saml_config?: {
    redirect_url?: string;
    saml_certificate?: string;
  };
}
interface AuthSettings {
  external_login_allowed?: boolean;
  internal?: LoginSettings;
  external?: LoginSettings;
  idle_session_timeout?: number;
}

type CourierPartner = {
  courierorgid: any;
  id: string;
  code: string;
  name: string;
  logo_url: string;
};
interface CustomFields {
  dropdownValue?: string[];
  id : number;
  name: string;
  description: string;
  isActive: boolean;
  dbKey: string;
  type?: string;
};

export enum PortTypes {
  WET = 'WET',
  DRY = 'DRY',
  AIR = 'AIR',
};

type AntdValidationError<FormFields = any> = {
  [key in keyof Partial<FormFields>]: {
    errors: {
      field: string;
      message: string;
    }[],
  }
} | undefined;

type valueof<T> = T[keyof T];

type EximType = {
  eximType: 'export' | 'import' | '';
}

type StyleProps<StylesType extends (...args: any) => any> = {
  classes: {
    [X in keyof ReturnType<StylesType>]: string;
  }
}

type FormFieldConfig = {
    id: string,
    posX?: number,
    posY?: number,
    width?: number,
    colspan?: number
    rowspan?: number
    customName?: string;
}

type StakeholderFilterParams = {
  stakeholderType: StakeholderTypes.SHIPPER | StakeholderTypes.CUSTOMER | StakeholderTypes.SUPPLIER;
  filterLabel: string;
  placeholder: string;
  options: { key: string; label: string }[];
  filterKey: 'shipperIds' | 'customerIds' | 'supplierIds';
}

export {
  AccessType, AccessTypeFilter, AccessTypes, Address, AntdValidationError, APIResponseType, AuthSettings, BookingMasterData, Carrier, ContainerDetails, CourierPartner, CustomFields, DefaultSortBy, EximType, FormFieldConfig, IdName, InquiryPortDetails, InquiryTypes, KeyLabel, KeyLabelObject, KeyValue, KeyValueMap, LabelValue, ModulePermissions, NextOrPrev, NotifyUsers, NumberString, PaginationActions, PaginationData, PaymentDetails, ProductAdditionalDetails, ProductDetails, ReeferProductTempRange, RouterProps, ShipmentTypes, ShippingLine, StakeholderFilterParams, StyleProps, Styles, StylesProps, TableColumn, Team, ThenArg, UserData, valueof, Weight
};
